import { makeVar, useLazyQuery } from '@apollo/client'
import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { REFRESH } from 'services/graphql/auth'
import LogRocket from 'logrocket'
import {
  forgotRoute,
  loginRoute,
  oidcCallbackRoute,
  resetRoute,
} from 'apps/meta/Auth/routes'
import { environment } from 'constants/server'
import { APP_TYPE } from 'constants/whereami'
import {
  endIntercomSession,
  getSessionTerminationGeneration,
} from 'components/Intercom/session'
import { safelyParseJSON } from 'utils/safelyParseJSON'

let timer: any

// A reset/expired session must keep the same shape that getSessionState()
// produces, so consumers reading eg session.permissions.* don't crash on a
// bare {}. Keep this in sync with getSessionState()'s derived fields.
const emptySession = () => ({
  permissions: {},
  isOnboarding: false,
  authorized: false,
})

// Onboarding status means "must set your own password before using the app",
// which the router enforces by redirecting to the reset-password screen. A
// session established via SSO (Okta/Google) skips that: the IdP credential is
// the user's credential, so there is no Flowhub password to force-reset. If the
// same user later logs in WITH a password, the session carries no 'sso' tag and
// the forced reset applies as before.
const deriveIsOnboarding = (session) =>
  session?.user?.status === 'onboarding' && session?.loginMethod !== 'sso'

let userContextSet: boolean = false
const identifyUser = (session) => {
  userContextSet = true
  LogRocket?.identify(session.user.email ?? 'unknown@example.com', {
    name: `${session.user.meta?.firstName} ${session.user.meta?.lastName}`,
    email: session.user.email ?? 'unknown@example.com',
    // we can pass any arbitrary string/num/bool as a user trait. these are custom:
    clientId: session.user.client?.id ?? 'Unknown Client',
    clientName: session.user.client?.name ?? 'Unknown Client',
    storeId: session.user.activeStore?.id ?? 'Unknown Store',
    storeName: session.user.activeStore?.name ?? 'Unknown Store',
    storeState: session.user.activeStore?.state ?? 'XX',
    userId: session.user.id ?? 'Unknown User',
    userIsInternal: session?.user?.isInternal ?? false,
    environmentEnv: environment,
    environmentPlatform: APP_TYPE,
  })
  if (APP_TYPE === 'iOS') {
    window.webkit?.messageHandlers?.identifyUser?.postMessage?.({
      userName: `${session.user.meta?.firstName} ${session.user.meta?.lastName}`,
      email: session.user.email ?? 'unknown@example.com',
      clientId: session.user.client?.id ?? 'Unknown Client',
      clientName: session.user.client?.name ?? 'Unknown Client',
      storeId: session.user.activeStore?.id ?? 'Unknown Store',
      storeName: session.user.activeStore?.name ?? 'Unknown Store',
    })
  }
  console.log('user identified:', session.user.email)
}

export const getSessionState = () => {
  const storedSession = localStorage.getItem('session')
  let sessionState = (storedSession && JSON.parse(storedSession)) || {}
  const permissions =
    sessionState?.user?.role?.permissions?.reduce((acc, perm) => {
      const target = perm.target
      const action = perm.action
      const modulator = perm.modulator

      if (acc[target]) {
        acc[target] = [...acc[target], modulator || action]
      } else {
        acc[target] = [modulator || action]
      }

      return acc
    }, {}) || {}

  sessionState.permissions = { ...permissions }
  sessionState.isOnboarding = deriveIsOnboarding(sessionState)
  sessionState.authorized = !!sessionState?.id
  return sessionState
}

// sessionRevisionVar is a change signal, NOT the session. getSessionState() reads
// localStorage imperatively, so consumers that read it (eg the SSE client) don't
// re-render when the session changes - they only pick up a new session on a full
// remount (ie a page refresh). useSession bumps this counter on every change, so
// components can subscribe via useReactiveVar to re-render and re-read
// getSessionState() on login/logout without a refresh.
//
// It deliberately carries no session data: the live session is always read from
// getSessionState(), so there is nothing here to drift out of sync, and nothing to
// misread as session state.
export const sessionRevisionVar = makeVar(0)

export const useSession = () => {
  //ToDo: properly type this!!!:
  const [session, setSession] = useState<any>(getSessionState)
  const location = useLocation()
  const previousUserId = useRef(session?.user?.id)
  const previouslyHadSession = useRef(!!session.id)
  // Tracks whether this page-load ever held an authorized session. We only want
  // the full-page reload below to fire on a genuine de-auth transition (logout /
  // token expiry), where it clears in-memory + Apollo state. On a cold load that
  // was never authorized, RequireAuth's <Redirect> already routes to /login
  // client-side, so reloading just throws that away and re-runs the whole boot
  // (river loader -> login -> reload -> river loader -> login).
  const wasAuthorizedRef = useRef(false)
  const [refresh, { loading: refreshLoading }] = useLazyQuery(REFRESH, {
    context: {
      headers: {
        Authorization: session.id || null,
      },
    },
  })
  useEffect(() => {
    if (
      (previouslyHadSession.current && !session.id) ||
      (previousUserId.current && previousUserId.current !== session?.user?.id)
    ) {
      endIntercomSession()
    }
    previouslyHadSession.current = !!session.id
    previousUserId.current = session?.user?.id
    localStorage.setItem('session', JSON.stringify(session))
    sessionRevisionVar(sessionRevisionVar() + 1)
    if (session?.isOnboarding === true && !deriveIsOnboarding(session)) {
      setSession({
        ...session,
        isOnboarding: deriveIsOnboarding(session),
      })
    }
    if (!!session.id !== session.authorized) {
      setSession({
        ...session,
        authorized: !!session.id,
        isOnboarding: deriveIsOnboarding(session),
      })
    }
    // TODO: this and o11y shouldnt be so coupled; tease apart
    if (session.user && !userContextSet) {
      identifyUser(session)
    }
  }, [session, session?.user?.status, session?.id])

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (
        event.storageArea !== localStorage ||
        (event.key !== 'session' && event.key !== null)
      )
        return
      const current = safelyParseJSON(localStorage.getItem('session') || '{}')
      if (
        session.id &&
        (!current?.id || current?.user?.id !== session?.user?.id)
      ) {
        endIntercomSession()
        // Do not persist this tab's stale state over the other tab's logout/login.
        window.location.reload()
      }
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [session.id, session?.user?.id])

  const refreshSession = async () => {
    // Capture per operation: another refresh must not make an older response
    // valid again after logout or a switch to a different account.
    const generation = getSessionTerminationGeneration()
    const stored = safelyParseJSON(localStorage.getItem('session') || '{}')
    if (!session.id || !stored?.id || stored?.user?.id !== session?.user?.id)
      return
    const isCurrent = () => {
      const current = safelyParseJSON(localStorage.getItem('session') || '{}')
      return (
        generation === getSessionTerminationGeneration() &&
        current?.id === stored.id &&
        current?.user?.id === stored?.user?.id
      )
    }
    try {
      const result = await refresh({
        variables: { refreshId: stored.refreshId || '' },
      })
      if (!isCurrent()) return result
      setSession(
        result?.data?.refresh?.id
          ? {
              ...result.data.refresh,
              authorized: true,
              loginMethod: session.loginMethod,
            }
          : emptySession()
      )
      return result
    } catch {
      if (isCurrent()) setSession(emptySession())
    }
  }

  useEffect(() => {
    clearTimeout(timer)
    if (session.authorized && session.expireTime) {
      let expireTimeToMs = new Date(session.expireTime * 1000)
      let dateNow = Date.now()
      let expiresIn = expireTimeToMs.getTime() - dateNow
      if (expiresIn < 0) {
        console.log(`token expired at ${expiresIn}, setting session to {}`)
        console.log(`token session.expireTime`, session.expireTime)
        console.log(`token expireTimeToMs`, expireTimeToMs)
        console.log(`token date.now`, dateNow)
        console.log(`token session`, session)
        setSession(emptySession())
        console.log('token after setSession({})')
      } else {
        timer = setTimeout(() => {
          console.log(`token expired at ${expiresIn}, refreshing session`)
          console.log(`token session.expireTime`, session.expireTime)
          console.log(`token expireTimeToMs`, expireTimeToMs)
          console.log(`token date.now`, dateNow)
          console.log(`token session`, session)
          refreshSession()
          console.log('token after refresh')
        }, expiresIn)
      }
    }
    /* 
      we only want to check this and set path if session has updated
      otherwise render loop is caused by history
    */
    if (session.authorized) {
      wasAuthorizedRef.current = true
    }
    const pathname = location.pathname?.toLowerCase() ?? ''
    const isLoginPath =
      pathname === loginRoute.toLowerCase() ||
      pathname === forgotRoute.toLowerCase() ||
      pathname === oidcCallbackRoute.toLowerCase()

    if (
      // redirect to login or forgot pw screen if not authorized. Only reload to
      // drop a session we actually held (logout/expiry); a never-authorized cold
      // load is handled client-side by RequireAuth's <Redirect>.
      (!session.authorized && wasAuthorizedRef.current && !isLoginPath) ||
      // redirect to reset password screen if authorized, in onboarding flow
      (session.authorized &&
        session.isOnboarding &&
        pathname !== resetRoute.toLowerCase() &&
        pathname !== oidcCallbackRoute.toLowerCase())
    ) {
      window.location.reload()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    session.isOnboarding,
    session.expireTime,
    session.authorized,
    session.refreshId,
    refresh,
  ])

  return [session, setSession, refreshSession, refreshLoading]
}
